import _classPrivateFieldGet from '@babel/runtime/helpers/classPrivateFieldGet';
import _classPrivateFieldSet from '@babel/runtime/helpers/classPrivateFieldSet';
import _defineProperty from '@babel/runtime/helpers/defineProperty';

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

var dedupeId = 0;
/**
 * @template {HTMLElement} BaseClass
 * @param {(base: BaseClass) => BaseClass} mixin
 */

function dedupeMixin(mixin) {
  var mixinId = dedupeId++;
  return base => {
    var _base$_appliedMixins, _base$_appliedMixins2;

    if ((_base$_appliedMixins = base._appliedMixins) !== null && _base$_appliedMixins !== void 0 && _base$_appliedMixins[mixinId]) {
      return base;
    }

    var extended = mixin(base);
    extended._appliedMixins = _objectSpread({}, (_base$_appliedMixins2 = base._appliedMixins) !== null && _base$_appliedMixins2 !== void 0 ? _base$_appliedMixins2 : {});
    extended._appliedMixins[mixinId] = true;
    return extended;
  };
}

function _classPrivateMethodInitSpec$1(obj, privateSet) { _checkPrivateRedeclaration$1(obj, privateSet); privateSet.add(obj); }

function _classPrivateFieldInitSpec(obj, privateMap, value) { _checkPrivateRedeclaration$1(obj, privateMap); privateMap.set(obj, value); }

function _checkPrivateRedeclaration$1(obj, privateCollection) { if (privateCollection.has(obj)) { throw new TypeError("Cannot initialize the same private elements twice on an object"); } }

function _classPrivateMethodGet$1(receiver, privateSet, fn) { if (!privateSet.has(receiver)) { throw new TypeError("attempted to get private field on non-instance"); } return fn; }
var VIEWPORT_POSITION_ATTR = 'viewport-position';
var VIEWPORT_POSITIONS = {
  aboveViewport: 'ABOVE-VIEWPORT',
  inViewport: 'IN-VIEWPORT',
  belowViewport: 'BELOW-VIEWPORT'
};
/**
 * Check if element represents the browser viewport
 *
 * @param viewportContainer Element to check
 * @returns {boolean} True if element represents the browser viewport and not a scroll container
 */

function isViewport(viewportContainer) {
  return (// eslint-disable-next-line compat/compat
    viewportContainer === document.scrollingElement || viewportContainer === document.documentElement
  );
}
/**
 * Custom Element mixin.
 *
 * Updates the `viewport-position` attribute according to whether the element is in the viewport or not.
 * Possible values for the attribute is `IN-VIEWPORT`, `ABOVE-VIEWPORT` and `BELOW-VIEWPORT`
 *
 * @template {HTMLElement} BaseClass
 * @param {BaseClass} Base
 */


function _ViewportPositionMixin(Base) {
  var _observer, _createIntersectionObserver, _disconnectIntersectionObserver, _handleIntersectionChange;

  return _observer = /*#__PURE__*/new WeakMap(), _createIntersectionObserver = /*#__PURE__*/new WeakSet(), _disconnectIntersectionObserver = /*#__PURE__*/new WeakSet(), _handleIntersectionChange = /*#__PURE__*/new WeakMap(), class extends Base {
    constructor() {
      super(...arguments);

      _classPrivateMethodInitSpec$1(this, _disconnectIntersectionObserver);

      _classPrivateMethodInitSpec$1(this, _createIntersectionObserver);

      _classPrivateFieldInitSpec(this, _observer, {
        writable: true,
        value: void 0
      });

      _classPrivateFieldInitSpec(this, _handleIntersectionChange, {
        writable: true,
        value: _ref => {
          var [intersection] = _ref;

          if (intersection.isIntersecting) {
            if (this.viewportPosition !== VIEWPORT_POSITIONS.inViewport) {
              this.viewportPosition = VIEWPORT_POSITIONS.inViewport;
            }
          } else {
            if (!this.viewportPosition || this.viewportPosition === VIEWPORT_POSITIONS.inViewport) {
              var bottom = this.getBoundingClientRect().bottom;
              var isAbove = bottom <= 0;
              this.viewportPosition = isAbove ? VIEWPORT_POSITIONS.aboveViewport : VIEWPORT_POSITIONS.belowViewport;
            }
          }
        }
      });
    }

    static get observedAttributes() {
      var _super$observedAttrib;

      return [...((_super$observedAttrib = super.observedAttributes) !== null && _super$observedAttrib !== void 0 ? _super$observedAttrib : []), VIEWPORT_POSITION_ATTR];
    }

    connectedCallback() {
      var _super$connectedCallb;

      (_super$connectedCallb = super.connectedCallback) === null || _super$connectedCallb === void 0 ? void 0 : _super$connectedCallb.call(this);

      _classPrivateMethodGet$1(this, _createIntersectionObserver, _createIntersectionObserver2).call(this);
    }

    disconnectedCallback() {
      var _super$disconnectedCa;

      (_super$disconnectedCa = super.disconnectedCallback) === null || _super$disconnectedCa === void 0 ? void 0 : _super$disconnectedCa.call(this);

      _classPrivateMethodGet$1(this, _disconnectIntersectionObserver, _disconnectIntersectionObserver2).call(this);
    }

    /**
     * Get the viewport or the scroll container element that is used as a replacement for the viewport.
     *
     * @returns {Element|HTMLElement} viewportContainer
     */
    get viewportContainer() {
      var _document$scrollingEl;

      /*
       * Using root/document.scrollingElement to represent the viewport,
       * as this presents a unified API for scrolling for both the viewport and scrolling elements.
       * (scrollTop, scrollLeft, scrollTo)
       *
       *  Some API's such as IntersectionObserver use different values for the viewport
       * (such as null and document), so take care.
       */
      // eslint-disable-next-line compat/compat
      return (_document$scrollingEl = document.scrollingElement) !== null && _document$scrollingEl !== void 0 ? _document$scrollingEl : document.documentElement;
    }
    /**
     * Get the intersection root
     * @returns {null|Element|HTMLElement|Document}
     */


    get viewportIntersectionRoot() {
      var viewportContainer = this.viewportContainer;

      if (isViewport(viewportContainer)) {
        var _navigator, _navigator$userAgent, _navigator$userAgent$;

        var isIFrame = window.location !== window.parent.location;
        var isChrome = (_navigator = navigator) === null || _navigator === void 0 ? void 0 : (_navigator$userAgent = _navigator.userAgent) === null || _navigator$userAgent === void 0 ? void 0 : (_navigator$userAgent$ = _navigator$userAgent.includes) === null || _navigator$userAgent$ === void 0 ? void 0 : _navigator$userAgent$.call(_navigator$userAgent, 'Chrome'); // Chrome has a bug when using document in as IntersectionObserver root: https://bugs.chromium.org/p/chromium/issues/detail?id=1420155

        if (isIFrame && !isChrome) {
          /* Use document as intersection root in iframes */
          return document;
        } else {
          return null;
        }
      } else {
        return viewportContainer;
      }
    }
    /**
     * Position of this element relative to the viewport.
     *
     * @type {'ABOVE-VIEWPORT' | 'IN-VIEWPORT' | 'BELOW-VIEWPORT' }
     */


    set viewportPosition(value) {
      var currentValue = this.getAttribute(VIEWPORT_POSITION_ATTR);

      if (value !== currentValue) {
        this.setAttribute(VIEWPORT_POSITION_ATTR, value);
      }
    }
    /**
     * Position of this element relative to the viewport.
     *
     * @type {'ABOVE-VIEWPORT' | 'IN-VIEWPORT' | 'BELOW-VIEWPORT' }
     */


    get viewportPosition() {
      return this.getAttribute(VIEWPORT_POSITION_ATTR);
    }

  };

  function _createIntersectionObserver2() {
    // eslint-disable-next-line compat/compat
    _classPrivateFieldSet(this, _observer, new IntersectionObserver(_classPrivateFieldGet(this, _handleIntersectionChange), {
      threshold: [0, 0.1, 0.5, 0.9, 1],
      // Intersection observer uses null or document for regular viewport
      root: this.viewportIntersectionRoot,
      // Scroll snapping elements trigger intersection when they are perfectly aligned with the screen,
      // both above and below the viewport. Using -1px as root margin to prevent intersection.
      rootMargin: '-1px 0px -1px 0px'
    }));

    _classPrivateFieldGet(this, _observer).observe(this);
  }

  function _disconnectIntersectionObserver2() {
    var _classPrivateFieldGet2, _classPrivateFieldGet3;

    (_classPrivateFieldGet2 = _classPrivateFieldGet(this, _observer)) === null || _classPrivateFieldGet2 === void 0 ? void 0 : _classPrivateFieldGet2.unobserve(this);
    (_classPrivateFieldGet3 = _classPrivateFieldGet(this, _observer)) === null || _classPrivateFieldGet3 === void 0 ? void 0 : _classPrivateFieldGet3.disconnect();
  }
}
/**
 * @template {HTMLElement} BaseClass
 * @param {BaseClass} Base */


var ViewportPositionMixin = dedupeMixin(_ViewportPositionMixin);

function _classPrivateMethodInitSpec(obj, privateSet) { _checkPrivateRedeclaration(obj, privateSet); privateSet.add(obj); }

function _checkPrivateRedeclaration(obj, privateCollection) { if (privateCollection.has(obj)) { throw new TypeError("Cannot initialize the same private elements twice on an object"); } }

function _classPrivateMethodGet(receiver, privateSet, fn) { if (!privateSet.has(receiver)) { throw new TypeError("attempted to get private field on non-instance"); } return fn; }

function _classStaticPrivateFieldSpecGet(receiver, classConstructor, descriptor) { _classCheckPrivateStaticAccess(receiver, classConstructor); _classCheckPrivateStaticFieldDescriptor(descriptor, "get"); return _classApplyDescriptorGet(receiver, descriptor); }

function _classApplyDescriptorGet(receiver, descriptor) { if (descriptor.get) { return descriptor.get.call(receiver); } return descriptor.value; }

function _classStaticPrivateFieldSpecSet(receiver, classConstructor, descriptor, value) { _classCheckPrivateStaticAccess(receiver, classConstructor); _classCheckPrivateStaticFieldDescriptor(descriptor, "set"); _classApplyDescriptorSet(receiver, descriptor, value); return value; }

function _classCheckPrivateStaticFieldDescriptor(descriptor, action) { if (descriptor === undefined) { throw new TypeError("attempted to " + action + " private static field before its declaration"); } }

function _classCheckPrivateStaticAccess(receiver, classConstructor) { if (receiver !== classConstructor) { throw new TypeError("Private static access of wrong provenance"); } }

function _classApplyDescriptorSet(receiver, descriptor, value) { if (descriptor.set) { descriptor.set.call(receiver, value); } else { if (!descriptor.writable) { throw new TypeError("attempted to set read only private field"); } descriptor.value = value; } }
/**
 * @typedef BoundAttributeConfig
 * @property {string} attribute – name of attribute to bind, or name of source and target attributes separated with a semicolon
 * @property {string | function } targets – selector string or function that takes element as argument and returns list of target elements
 */

/**
 * Custom element mixin that creates a one way binding between an attribute on
 * the custom element and a given attribute on it's children.
 *
 * @template {HTMLElement} BaseClass
 * @param {BaseClass} Base
 */

function _AttributeBindingMixin(Base) {
  var _class, _getTargetElements, _boundAttributes;

  return _getTargetElements = /*#__PURE__*/new WeakSet(), (_class = class AttributeBindingMixinDef extends Base {
    constructor() {
      super(...arguments);

      _classPrivateMethodInitSpec(this, _getTargetElements);
    }

    /**
     * Configure the bound attributes.
     *
     * @returns {BoundAttributeConfig[]}
     */
    static get boundAttributes() {
      return [];
    }

    static get observedAttributes() {
      var _super$observedAttrib;

      var boundAttributes = {}; // Parse and store configuration for faster lookup

      for (var {
        attribute,
        targets
      } of this.boundAttributes) {
        if (typeof attribute !== 'string') {
          throw Error('Bound attribute configuration must have `attribute` property of string type');
        }

        var [sourceAttribute, targetAttribute = sourceAttribute] = attribute.split(':');
        boundAttributes[sourceAttribute] = {
          sourceAttributeName: sourceAttribute,
          targetAttributeName: targetAttribute,
          targets
        };
      }

      _classStaticPrivateFieldSpecSet(AttributeBindingMixinDef, _class, _boundAttributes, boundAttributes); // Observe bound attributes


      return [...((_super$observedAttrib = super.observedAttributes) !== null && _super$observedAttrib !== void 0 ? _super$observedAttrib : []), ...Object.keys(boundAttributes)];
    }

    attributeChangedCallback(name, oldValue, newValue) {
      var _super$attributeChang;

      (_super$attributeChang = super.attributeChangedCallback) === null || _super$attributeChang === void 0 ? void 0 : _super$attributeChang.call(this, name, oldValue, newValue);

      if (newValue === oldValue) {
        return;
      }

      var boundAttributes = _classStaticPrivateFieldSpecGet(AttributeBindingMixinDef, _class, _boundAttributes);

      if (name in boundAttributes) {
        var configuration = boundAttributes[name];
        var targets = configuration.targets;

        var targetElements = _classPrivateMethodGet(this, _getTargetElements, _getTargetElements2).call(this, targets);

        for (var element of targetElements) {
          if (newValue !== null) {
            element.setAttribute(configuration.targetAttributeName, newValue);
          } else {
            element.removeAttribute(configuration.targetAttributeName);
          }
        }
      }
    }

  }, _boundAttributes = {
    writable: true,
    value: void 0
  }, _class);

  function _getTargetElements2(targets) {
    if (typeof targets === 'function') {
      return targets(this);
    } else if (typeof targets === 'string') {
      return Array.from(this.querySelectorAll(targets));
    } else {
      throw new Error("Unsupported targets type: ".concat(typeof targets));
    }
  }
}
/**
 * @template {HTMLElement} BaseClass
 * @param {BaseClass} Base
 */


var AttributeBindingMixin = dedupeMixin(_AttributeBindingMixin);

export { AttributeBindingMixin, VIEWPORT_POSITIONS, VIEWPORT_POSITION_ATTR, ViewportPositionMixin };
//# sourceMappingURL=dh-basis-mixins.esm.js.map
